import { useCallback, useEffect, useRef, useState } from "react";
import { INiche } from "@/common/services/types";
import { apiNiches } from "@/common/services";
import { PaginatedResponse, usePaginatedFetch } from "@/common/hooks/usePaginatedFetch";

const PAGE_SIZE = 30;

interface IProps {
  initialOrder?: string;
  count?: number;
  tag?: string;
}

interface IUseFilterNichesQuery {
  niches: INiche[];
  isLoading: boolean;
  hasMore: boolean;
  handleFetchMore: () => void;
  order: string;
  sort: string;
  handleOrderChange: (order: string, sort: string) => void;
}

export const useFilterNichesQuery = (props?: IProps): IUseFilterNichesQuery => {
  const { initialOrder = "subscribers", count = PAGE_SIZE, tag } = props || {};

  const sleepTime = 100;

  const [order, setOrder] = useState<string>(initialOrder);
  const [sort, setSort] = useState<string>("desc");

  const latestOrder = useRef(order);
  const latestSort = useRef(sort);

  useEffect(() => {
    latestOrder.current = order;
    latestSort.current = sort;
  }, [order, sort]);

  const isUpdating = useRef(false);

  const fetchFunc = useCallback(
    async (page: number = 1): Promise<PaginatedResponse<INiche>> => {
      const res = await apiNiches.getNiches({
        order,
        sort,
        page: page,
        previews: true,
        count,
        tag,
      });
      return {
        items: res.niches,
        page,
        pages: res.pages,
        total: res.total,
      };
    },
    [order, sort, count, tag]);

  const pager = usePaginatedFetch<INiche>(fetchFunc);

  const handleOrderChange = useCallback((newOrder: string, newSort: string) => {
    if (isUpdating.current) {
      return;
    }

    if (newOrder === latestOrder.current && newSort === latestSort.current) {
      return;
    }

    isUpdating.current = true;

    try {
      setOrder(newOrder);
      setSort(newSort);
    } finally {
      setTimeout(() => {
        isUpdating.current = false;
      }, sleepTime);
    }
  }, []);

  return {
    niches: pager.items,
    isLoading: pager.isLoading,
    hasMore: pager.hasMore,
    handleFetchMore: pager.loadMore,
    order,
    sort,
    handleOrderChange,
  };
};
